import { useState, useRef } from 'react';
import styled from 'styled-components';
import {
  PIN_CODE,
  MAX_PIN_ATTEMPTS,
  savePinAuth,
  getPinAttempts,
  incrementPinAttempts,
} from '../utils/pinAuth';


export function usePinInput({ onSuccess, autoSubmit = false }) {
  const [pin, setPin] = useState(['', '', '', '']);
  const [pinError, setPinError] = useState(false);
  const [shake, setShake] = useState(false);
  const [attempts, setAttempts] = useState(() => getPinAttempts());
  const [locked, setLocked] = useState(() => getPinAttempts() >= MAX_PIN_ATTEMPTS);
  const inputRefs = useRef([]);

  const reset = () => {
    setPin(['', '', '', '']);
    setPinError(false);
  };

  const verify = (entered) => {
    if (entered === PIN_CODE) {
      savePinAuth();
      reset();
      onSuccess();
    } else {
      const n = incrementPinAttempts();
      setAttempts(n);
      if (n >= MAX_PIN_ATTEMPTS) setLocked(true);
      setPinError(true);
      setShake(true);
      setTimeout(() => {
        setPin(['', '', '', '']);
        setShake(false);
        if (n < MAX_PIN_ATTEMPTS) inputRefs.current[0]?.focus();
      }, 500);
    }
  };

  const handleChange = (i, val) => {
    if (locked) return;
    if (val.length > 1) return;
    if (val && !/^\d$/.test(val)) return;
    const next = [...pin];
    next[i] = val;
    setPin(next);
    setPinError(false);
    if (val && i < 3) inputRefs.current[i + 1]?.focus();
    if (autoSubmit && next.every(d => d !== '')) verify(next.join(''));
  };

  const handleKeyDown = (i, e) => {
    if (locked) return;
    if (e.key === 'Backspace' && !pin[i] && i > 0) inputRefs.current[i - 1]?.focus();
    if (e.key === 'Enter') submit();
  };

  const submit = () => {
    if (pin.join('').length < 4) return;
    verify(pin.join(''));
  };

  return { pin, pinError, shake, attempts, locked, inputRefs, handleChange, handleKeyDown, submit, reset };
}


const Container = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 1.5rem;
`;

const Digit = styled.input`
  width: 56px;
  height: 64px;
  text-align: center;
  font-size: 1.75rem;
  font-weight: 700;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', monospace;
  border: 2px solid ${({ $filled }) => ($filled ? 'rgba(102, 126, 234, 0.8)' : 'rgba(255, 255, 255, 0.15)')};
  border-radius: 12px;
  background: ${({ $filled }) => ($filled ? 'rgba(102, 126, 234, 0.15)' : 'rgba(255, 255, 255, 0.05)')};
  color: #ffffff;
  transition: all 0.2s ease;
  -webkit-text-security: disc;
  outline: none;

  &:focus {
    border-color: #667eea;
    background: rgba(102, 126, 234, 0.2);
    box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.2);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMsg = styled.div`
  color: #ff6b6b;
  font-size: 0.85rem;
  font-weight: 500;
  margin-top: -0.5rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

const AttemptsMsg = styled.div`
  font-size: 0.8rem;
  color: ${({ $critical }) => ($critical ? '#ff6b6b' : 'rgba(255, 255, 255, 0.5)')};
  margin-top: 0.5rem;
`;

export function PinInputGroup({ pin, onChange, onKeyDown, inputRefs, pinError, attempts }) {
  return (
    <>
      <Container>
        {pin.map((d, i) => (
          <Digit
            key={i}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={d}
            $filled={d !== ''}
            onChange={e => onChange(i, e.target.value)}
            onKeyDown={e => onKeyDown(i, e)}
            ref={el => (inputRefs.current[i] = el)}
            autoFocus={i === 0}
          />
        ))}
      </Container>

      {pinError && (
        <ErrorMsg>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          Incorrect PIN. Please try again.
        </ErrorMsg>
      )}

      {attempts > 0 && (
        <AttemptsMsg $critical={attempts >= 2}>
          {MAX_PIN_ATTEMPTS - attempts} attempt{MAX_PIN_ATTEMPTS - attempts !== 1 ? 's' : ''} remaining
        </AttemptsMsg>
      )}
    </>
  );
}
